/* GSD-860: shared Shopify consumer of the GSD-903 guest booking API. */
(() => {
  if (customElements.get("sw-booking-flow")) return;
  // Deduplicate conversions without sending booking tokens to analytics.
  const trackedInstantBookings = new Set();
  function trackInstantBooking(token) {
    if (typeof token !== "string" || !token || trackedInstantBookings.has(token)) return;
    trackedInstantBookings.add(token);
    try {
      const consent = window.Cookiebot?.consent;
      // No queued conversions before consent, and no retroactive replay after refusal.
      if (consent?.statistics !== true || consent?.marketing !== true) return;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "booking_completed",
        booking_type: "instant",
        journey: "booking",
      });
    } catch {
      // An analytics integration failure must never interrupt an accepted booking.
    }
  }
  window.SmileWhiteBookingTracking = Object.freeze({ instantAccepted: trackInstantBooking });
  const MILLISECONDS_PER_DAY = 86_400_000;
  const MILLISECONDS_PER_SECOND = 1_000;
  const MILLISECONDS_PER_MINUTE = 60_000;
  const MAX_AVAILABILITY_RANGE_DAYS = 90;
  const INLINE_DAY_LOOKAHEAD_DAYS = 2;
  const INLINE_DAY_CHOICE_COUNT = 2;
  const REQUEST_TIMEOUT_MS = 20_000;
  const CONTACT_REFRESH_BUFFER_MS = 10_000;
  const MEETING_POLL_LIMIT = 60;
  const MEETING_WAIT_NOTICE_MS = 120_000;
  const MEETING_POLL_INITIAL_MS = 5_000;
  const MEETING_POLL_MAX_MS = 20_000;
  const MEETING_POLL_BACKOFF_FACTOR = 1.5;
  const AVAILABILITY_STATES = Object.freeze({
    IDLE: 'idle', LOADING: 'loading', READY: 'ready', ERROR: 'error',
  });
  const BOOKING_CHOICES = Object.freeze({ INSTANT: 'instant', SCHEDULED: 'scheduled' });
  const BOOKING_STATUSES = Object.freeze({ INSTANT: 'instant_booking', SCHEDULED: 'scheduled' });
  window.SmileWhiteBookingStates = Object.freeze({ availability: AVAILABILITY_STATES, choices: BOOKING_CHOICES });
  const JOURNEY_STEPS = Object.freeze({ DETAILS: 'details', APPOINTMENT: 'appointment', COMPLETE: 'complete' });
  const SCHEDULED_JOURNEY = Object.freeze([JOURNEY_STEPS.DETAILS, JOURNEY_STEPS.APPOINTMENT, JOURNEY_STEPS.COMPLETE]);
  const INSTANT_JOURNEY = Object.freeze([JOURNEY_STEPS.DETAILS, JOURNEY_STEPS.COMPLETE]);

  function instantActionDisabled(state) {
    const submissionLocked = state.busy || state.cooldown || state.bookingAttempt || state.leadAttempt || state.bookingToken;
    const discoveryPending = state.instantDiscoveryState === AVAILABILITY_STATES.LOADING;
    const retryCoolingDown = state.instantRetryAt > Date.now();
    const noAvailableAction = !state.instantSlot && state.instantDiscoveryState !== AVAILABILITY_STATES.ERROR;
    return Boolean(submissionLocked || discoveryPending || retryCoolingDown || noAvailableAction);
  }
  const ATTRIBUTION_KEYS = [
    "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content",
    "gclid", "fbclid", "msclkid",
  ];
  const ATTRIBUTION_COOKIE_MAX_AGE = 30 * 24 * 60 * 60;
  const MAX_ATTRIBUTION_VALUE_LENGTH = 1_000;
  const MAX_BOOKING_METADATA_LENGTH = 8_192;
  const FORM_SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{0,79}$/;
  const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
  const PHONE_PATTERN = /^\+?[0-9 ()-]{10,24}$/;
  const TRUSTPILOT_BOOTSTRAP_SRC = "https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js";
  let trustpilotBootstrap;

  function captureBookingAttribution() {
    const params = new URL(location.href).searchParams;
    const cookies = new Map();
    try {
      for (const cookie of document.cookie.split(";")) {
        const separator = cookie.indexOf("=");
        if (separator === -1) continue;
        try {
          const name = decodeURIComponent(cookie.slice(0, separator).trim());
          if (ATTRIBUTION_KEYS.includes(name) && !cookies.has(name))
            cookies.set(name, decodeURIComponent(cookie.slice(separator + 1)));
        } catch { /* Ignore malformed cookies without interrupting booking. */ }
      }
    } catch { /* Direct-link attribution still works when cookies are blocked. */ }
    const tracking = {};
    for (const key of ATTRIBUTION_KEYS) {
      // Reuse the storefront's last-touch cookies, including visits before booking.
      const incoming = params.get(key);
      const value = incoming || cookies.get(key);
      if (!value || value.length > MAX_ATTRIBUTION_VALUE_LENGTH) continue;
      tracking[key] = value;
      if (incoming) {
        try {
          // Standalone booking pages do not load the cart attribution script.
          const attributes = [
            `${key}=${encodeURIComponent(value)}`, "Path=/",
            `Max-Age=${ATTRIBUTION_COOKIE_MAX_AGE}`, "SameSite=Lax",
          ];
          if (location.protocol === "https:") attributes.push("Secure");
          document.cookie = attributes.join("; ");
        } catch { /* Cookie restrictions must not prevent booking. */ }
      }
    }
    return tracking;
  }

  function loadTrustpilot(element) {
    if (!element || element.dataset.trustpilotLoaded === "true") return;
    trustpilotBootstrap ||= new Promise((resolve, reject) => {
      if (window.Trustpilot?.loadFromElement) return resolve();
      const script = document.createElement("script");
      script.src = TRUSTPILOT_BOOTSTRAP_SRC;
      script.async = true;
      script.onload = resolve;
      script.onerror = reject;
      document.head.append(script);
    });
    trustpilotBootstrap.then(() => {
      if (!element.isConnected || !window.Trustpilot?.loadFromElement) return;
      window.Trustpilot.loadFromElement(element, true);
      element.dataset.trustpilotLoaded = "true";
    }).catch(() => { /* The Trustpilot profile link remains available. */ });
  }
  const BOOKING_STEPS = Object.freeze({
    SELECT: 0,
    DETAILS: 1,
    DAY: 2,
    TIME: 3,
    COMPLETE: 4,
  });

  const LOCAL_HOSTS = ["localhost", "127.0.0.1", "[::1]", "sw-shopify.local"];
  const CONTACT_FIELDS = [
    {
      name: "firstName",
      label: "First Name",
      required: true,
      half: true,
      autocomplete: "given-name",
      maxLength: 100,
    },
    {
      name: "lastName",
      label: "Last Name",
      required: true,
      half: true,
      autocomplete: "family-name",
      maxLength: 100,
    },
    {
      name: "email",
      label: "Email",
      type: "email",
      required: true,
      autocomplete: "email",
      maxLength: 254,
    },
    {
      name: "phoneNumber",
      label: "Phone Number",
      type: "tel",
      required: true,
      autocomplete: "tel",
      placeholder: "07XXX XXXXXX",
      maxLength: 24,
    },
  ];
  const icons = {
    clock: '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>',
    shield:
      '<path d="M12 3l8 4v6c0 5-8 8-8 8s-8-3-8-8V7l8-4z"/><path d="m8 12 3 3 5-6"/>',
    gift: '<path d="M4 10h16v11H4zM3 6h18v4H3zM12 6v15"/><path d="M12 6H8a2.5 2.5 0 1 1 2.5-2.5L12 6zm0 0h4a2.5 2.5 0 1 0-2.5-2.5L12 6z"/>',
  };
  const messages = {
    INVALID_REQUEST: "Please check your details and try again.",
    CONTACT_INVALID: "Please check your email address and phone number.",
    REFERRAL_INVALID: "Please check your referral code.",
    VALIDATION_REQUIRED: "Please recheck your details before continuing.",
    SLOT_UNAVAILABLE:
      "That time is no longer available. Please choose another time.",
    SLOT_FULL: "That time has just been booked. Please choose another time.",
    BOOKING_EXISTS:
      "You already have an active booking. Please use the link in your confirmation to change it.",
    RATE_LIMITED: "Please wait a moment before trying again.",
    SERVICE_BUSY: "Please wait a moment before trying again.",
  };
  const make = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const dateKey = (date, timezone) =>
    new Intl.DateTimeFormat("en-CA", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(date);
  const addDays = (day, count) =>
    new Date(Date.parse(`${day}T12:00:00Z`) + count * MILLISECONDS_PER_DAY)
      .toISOString()
      .slice(0, 10);
  const createIdempotencyKey = () => {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 15) | 64;
    bytes[8] = (bytes[8] & 63) | 128;
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  };
  function validateFormConfiguration(form) {
    const horizonIsValid =
      Number.isSafeInteger(form.daysIntoFuture) && form.daysIntoFuture >= 0;
    if (!Array.isArray(form.questions) || !form.timezone || !horizonIsValid)
      throw Error("Invalid form response");
    const today = dateKey(new Date(), form.timezone);
    addDays(today, form.daysIntoFuture);
    const names = new Set(CONTACT_FIELDS.map((field) => field.name));
    for (const field of form.questions) {
      const supportedType = [
        "text",
        "textarea",
        "select",
        "checkbox",
        "discount_code",
      ].includes(field.type);
      const unsafeName = ["__proto__", "constructor", "prototype"].includes(
        field.name,
      );
      if (!supportedType || !field.name || unsafeName || names.has(field.name))
        throw Error("Unsupported question");
      names.add(field.name);
    }
  }

  function shiftMonth(month, direction) {
    const date = new Date(`${month}-01T12:00:00Z`);
    date.setUTCMonth(date.getUTCMonth() + direction);
    return date.toISOString().slice(0, 7);
  }

  const CALENDAR_WINDOW_DAYS = 5;

  function getMonthRange(month, today, lastDay) {
    const firstDayOfMonth = `${month}-01`;
    const lastDayOfMonth = addDays(`${shiftMonth(month, 1)}-01`, -1);
    return {
      from: firstDayOfMonth < today ? today : firstDayOfMonth,
      to: lastDayOfMonth > lastDay ? lastDay : lastDayOfMonth,
    };
  }

  function formatDateLabel(day) {
    return new Intl.DateTimeFormat("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
      timeZone: "UTC",
    }).format(new Date(`${day}T12:00:00Z`));
  }

  function resolveApiBase(configuredBase) {
    const local = LOCAL_HOSTS.includes(location.hostname);
    const configured = configuredBase?.trim();
    const base = new URL(
      configured || (local ? "http://sw-bookingtool.local/v1/booking" : ""),
    );
    if (
      base.username ||
      base.password ||
      base.search ||
      base.hash ||
      !base.pathname.endsWith("/v1/booking")
    )
      throw Error("Invalid API configuration");
    if (
      base.protocol !== "https:" &&
      !(
        local &&
        base.protocol === "http:" &&
        [...LOCAL_HOSTS, "sw-bookingtool.local"].includes(base.hostname)
      )
    )
      throw Error("Invalid API configuration");
    if (
      !local &&
      [...LOCAL_HOSTS, "sw-bookingtool.local"].includes(base.hostname)
    )
      throw Error("Invalid API configuration");
    return base.href.replace(/\/$/, "");
  }

  function getCompletionUrl(completionPath) {
    const path = completionPath || "/pages/aligners-thank-you-page";
    if (!/^\/pages\/[a-z0-9][a-z0-9-]*\/?$/i.test(path))
      throw Error("Invalid completion destination");
    return new URL(path, location.origin).href;
  }

  function resolveBookingSource(bookingUrl, legacyFormSlug) {
    const configured = bookingUrl?.trim();
    if (!configured) {
      if (!FORM_SLUG_PATTERN.test(legacyFormSlug || "")) throw Error("Invalid form");
      return { type: "forms", slug: legacyFormSlug };
    }
    const url = new URL(configured, location.origin);
    const local = LOCAL_HOSTS.includes(location.hostname);
    const localBooking = local && [...LOCAL_HOSTS, "sw-bookingtool.local"].includes(url.hostname);
    const supportedOrigin = url.origin === location.origin ||
      (url.hostname === "booking.smilewhite.co.uk" && !url.port) || localBooking;
    if (url.username || url.password || !supportedOrigin ||
        (url.protocol !== "https:" && !(localBooking && url.protocol === "http:")))
      throw Error("Invalid booking URL");
    // The public URL selects a resource. It never supplies the API host or query.
    const groupMatch = url.pathname.match(/^\/groups\/([a-z0-9][a-z0-9-]{0,79})\/?$/);
    if (groupMatch) return { type: "groups", slug: groupMatch[1] };
    const formMatch = url.pathname.match(/^\/booking\/([a-z0-9][a-z0-9-]{0,79})\/?$/);
    if (formMatch) return { type: "forms", slug: formMatch[1] };
    if (/^\/booking\/?$/.test(url.pathname)) return { type: "forms", slug: "default" };
    throw Error("Invalid booking URL");
  }

  function validateGroupConfiguration(group) {
    if (!group || typeof group.name !== "string" || !FORM_SLUG_PATTERN.test(group.slug || "") ||
        !Array.isArray(group.forms) || typeof group.skipSelection !== "boolean")
      throw Error("Invalid group response");
    const slugs = new Set();
    for (const form of group.forms) {
      if (!FORM_SLUG_PATTERN.test(form.slug || "") || slugs.has(form.slug) ||
          typeof form.name !== "string" || !form.name.trim() ||
          (form.description !== undefined && typeof form.description !== "string") ||
          (form.address != null && typeof form.address !== "string"))
        throw Error("Invalid group member");
      slugs.add(form.slug);
    }
    return group;
  }

  class BookingApiClient {
    constructor(configuredBase, formSlug, signal, resource = "forms") {
      if (!FORM_SLUG_PATTERN.test(formSlug)) throw Error("Invalid form");
      if (!["forms", "groups"].includes(resource)) throw Error("Invalid booking resource");
      this.base = resolveApiBase(configuredBase);
      this.formSlug = formSlug;
      this.signal = signal;
      this.resource = resource;
    }

    async getForm() {
      const form = await this.request("");
      validateFormConfiguration(form);
      return form;
    }

    async getAvailability(from, to) {
      const dates = [];
      for (
        let rangeStart = from;
        rangeStart <= to;
        rangeStart = addDays(rangeStart, MAX_AVAILABILITY_RANGE_DAYS + 1)
      ) {
        const maximumRangeEnd = addDays(
          rangeStart,
          MAX_AVAILABILITY_RANGE_DAYS,
        );
        const rangeEnd = maximumRangeEnd > to ? to : maximumRangeEnd;
        const result = await this.request(
          `/availability?from=${rangeStart}&to=${rangeEnd}`,
        );
        dates.push(...result.dates);
      }
      return dates;
    }

    async request(path, body, key, token) {
      const headers = { Accept: "application/json" };
      if (body) headers["Content-Type"] = "application/json";
      if (key) headers["Idempotency-Key"] = key;
      if (token) headers.Authorization = `Bearer ${token}`;
      const signal = AbortSignal.any([
        this.signal,
        AbortSignal.timeout(REQUEST_TIMEOUT_MS),
      ]);
      const response = await fetch(
        token ? `${this.base}/bookings/current${path}` : `${this.base}/${this.resource}/${encodeURIComponent(this.formSlug)}${path}`,
        {
          method: body ? "POST" : "GET",
          headers,
          body: body ? JSON.stringify(body) : undefined,
          credentials: "omit",
          cache: "no-store",
          referrerPolicy: "no-referrer",
          signal,
        },
      );
      let result;
      try {
        result = await response.json();
      } catch {
        throw Error("Invalid API response");
      }
      signal.throwIfAborted();
      if (!response.ok) {
        const error = new Error("Booking request rejected");
        error.code = result?.error?.code;
        error.retryAfter = Math.min(
          300,
          Math.max(1, Number(response.headers.get("Retry-After")) || 1),
        );
        throw error;
      }
      return result;
    }

    async validateDetails(details, form) {
      const validation = await this.request("/validate-contact", {
        email: details.email,
        phoneNumber: details.phoneNumber,
      });
      if (!validation.valid || !validation.proof || !validation.expiresAt) {
        const error = Error("Contact invalid");
        error.code = "CONTACT_INVALID";
        throw error;
      }
      const payload = {
        firstName: details.firstName,
        lastName: details.lastName,
        email: details.email,
        phoneNumber: details.phoneNumber,
        metadata: { ...details.metadata },
        contactProof: validation.proof,
      };
      let expiry = validation.expiresAt;
      for (const field of form.questions.filter(
        (field) => field.type === "discount_code",
      )) {
        if (!payload.metadata[field.name]) continue;
        const referral = await this.request("/validate-referral", {
          code: payload.metadata[field.name],
        });
        if (referral.status === "invalid") {
          const error = Error("Referral invalid");
          error.code = "REFERRAL_INVALID";
          throw error;
        }
        if (!["valid", "unavailable"].includes(referral.status))
          throw Error("Invalid referral response");
        const normalisedCode = referral.normalizedCode;
        if (normalisedCode) payload.metadata[field.name] = normalisedCode;
        if (referral.proof) payload.referralProof = referral.proof;
        if (referral.expiresAt) expiry = Math.min(expiry, referral.expiresAt);
      }
      return { payload, expiry };
    }
  }

  window.SmileWhiteBookingApiClient = BookingApiClient;

  class BookingView {
    constructor(element) {
      this.element = element;
    }

    renderFacts(form) {
      const facts = this.element
        .closest(".booking-experience")
        ?.querySelector("[data-booking-facts]");
      if (facts)
        facts.replaceChildren(
          make("span", "", form.name),
          make("span", "", `${form.slotDuration} minutes`),
        );
    }

    renderGroup(group) {
      const container = this.element.querySelector("[data-form-options]");
      container.replaceChildren();
      for (const form of group.forms) {
        const button = make("button", "sw-booking__form-option");
        button.type = "button";
        button.dataset.selectForm = form.slug;
        button.append(make("strong", "", form.name));
        if (form.description) button.append(make("span", "", form.description));
        if (form.address) button.append(make("small", "", form.address));
        container.append(button);
      }
      this.element.querySelector("[data-title]").textContent = group.name;
      this.element.querySelector("[data-group-empty]").hidden = group.forms.length > 0;
      this.element.querySelector("[data-duration]").replaceChildren();
      this.element.querySelector("[data-duration]").hidden = true;
      this.element.querySelector("[data-context]").hidden = true;
      this.element.querySelector(".sw-booking__promotion").hidden = true;
      const facts = this.element.closest(".booking-experience")?.querySelector("[data-booking-facts]");
      if (facts) facts.replaceChildren(make("span", "", group.name));
    }

    updateControls(state) {
      const disabled = state.busy || state.cooldown;
      this.element.setAttribute("aria-busy", String(state.busy));
      this.element
        .querySelectorAll("button, input, textarea, select")
        .forEach((node) => {
          node.disabled = Boolean(disabled);
          if ((state.dayTabs || state.singleRowCalendar) && node.tagName === "BUTTON") {
            const activeRequest = state.busy && node === state.busyControl;
            const instantLookup = node.hasAttribute("data-choose-instant") && state.instantDiscoveryState === AVAILABILITY_STATES.LOADING;
            node.setAttribute("aria-busy", String(activeRequest || instantLookup));
          }
        });
      this.element.querySelector('[type="submit"]').disabled = Boolean(
        disabled || !state.flow || state.bookingAttempt,
      );
      this.element.querySelector("[data-book]").disabled = Boolean(
        disabled || !state.selectedSlot,
      );
      if (state.bookingAttempt) {
        this.element.querySelectorAll("[data-back], [data-day], [data-prev-month], [data-next-month]").forEach(control => { control.disabled = true; });
        this.element.querySelectorAll("[data-slot]").forEach((node) => {
          node.disabled = true;
        });
      }
      const changeForm = this.element.querySelector("[data-change-form]");
      if (changeForm) changeForm.disabled = Boolean(disabled || state.bookingAttempt || state.leadAttempt);
      this.element.querySelectorAll('[data-choose-instant]').forEach(control => {
        control.disabled = instantActionDisabled(state);
      });
      if (state.leadAttempt || state.bookingAttempt)
        this.element
          .querySelectorAll(
            "[data-fields] input, [data-fields] textarea, [data-fields] select",
          )
          .forEach((node) => {
            node.disabled = true;
          });
      const status = this.element.querySelector("[data-status]");
      status.hidden = !state.busy || state.singleRowCalendar;
      status.textContent = state.busy
        ? state.bookingAttempt
          ? "Booking your appointment…"
          : "Loading…"
        : "";
    }

    showError(message) {
      const node = this.element.querySelector("[data-error]");
      node.textContent = message;
      node.hidden = false;
    }

    renderIntro(form, presentation) {
      const title = this.element.querySelector("[data-title]");
      if (presentation !== "split") {
        const words = form.bookingTitle.trim().split(/\s+/);
        let split = 1;
        let distance = Infinity;
        for (let index = 1; index < words.length; index++) {
          const next = Math.abs(
            words.slice(0, index).join(" ").length - words.join(" ").length / 2,
          );
          if (next < distance) {
            split = index;
            distance = next;
          }
        }
        title.replaceChildren(
          document.createTextNode(words.slice(0, split).join(" ")),
          make("span", "", words.slice(split).join(" ")),
        );
      } else title.textContent = form.bookingTitle;
      const duration = this.element.querySelector("[data-duration]");
      duration.hidden = false;
      const clock = make("span", "sw-booking__duration-icon");
      clock.setAttribute("aria-hidden", "true");
      clock.innerHTML = `<svg viewBox="0 0 24 24">${icons.clock}</svg>`;
      duration.replaceChildren(
        clock,
        make("span", "", `${form.slotDuration} minutes`),
      );
      this.element.querySelector("[data-promotion]").textContent =
        form.banner?.text || "";
      this.element.querySelector(".sw-booking__promotion").hidden =
        !form.banner?.text;
      const benefits = this.element.querySelector("[data-benefits]");
      benefits.replaceChildren();
      (form.benefits || []).forEach((benefit) => {
        const row = make(
          "div",
          `sw-booking__benefit${benefit.variant === "highlight" ? " sw-booking__benefit--highlight" : ""}`,
        );
        const symbol = make("span", "sw-booking__benefit-icon");
        symbol.setAttribute("aria-hidden", "true");
        // Only static icon markup is parsed; API copy uses textContent.
        symbol.innerHTML = `<svg viewBox="0 0 24 24">${icons[benefit.icon] || icons.clock}</svg>`;
        const copy = make("div");
        copy.append(
          make("strong", "", benefit.title),
          make("p", "", benefit.body),
        );
        row.append(symbol, copy);
        benefits.append(row);
      });
    }

    renderFields(form, presentation, signal) {
      const container = this.element.querySelector("[data-fields]");
      container.replaceChildren();
      this.element.querySelector("[data-details]").noValidate = true;
      [...CONTACT_FIELDS, ...form.questions].forEach((field) => {
        const id = `booking-${this.element.dataset.sectionId}-${field.name}`;
        const label = make(
          "label",
          field.type === "checkbox"
            ? "sw-booking__consent"
            : `sw-booking__field${field.half ? " sw-booking__field--half" : ""}`,
        );
        label.htmlFor = id;
        const input = make(
          field.type === "textarea"
            ? "textarea"
            : field.type === "select"
              ? "select"
              : "input",
        );
        input.id = id;
        input.name = field.name;
        input.dataset.fieldLabel = field.label;
        if (input.tagName === "INPUT")
          input.type = ["checkbox", "email", "tel"].includes(field.type)
            ? field.type
            : "text";
        input.required = Boolean(field.required);
        input.maxLength =
          field.maxLength ||
          (field.type === "textarea"
            ? 1000
            : field.type === "discount_code"
              ? 64
              : 254);
        input.autocomplete = field.autocomplete || "off";
        if (field.placeholder) input.placeholder = field.placeholder;
        else if (CONTACT_FIELDS.includes(field))
          input.placeholder = field.label;
        if (field.name === "age") {
          input.inputMode = "numeric";
          input.pattern = "[0-9]{1,3}";
          input.maxLength = 3;
          input.addEventListener(
            "input",
            () =>
              input.setCustomValidity(
                input.value && Number(input.value) < 15
                  ? "We can only treat patients aged 15 and above."
                  : "",
              ),
            { signal: signal },
          );
        }
        if (field.type === "select") {
          const placeholder = make("option", "", "Select an option");
          placeholder.value = "";
          input.append(placeholder);
          (field.choices || []).forEach((choice) => {
            const option = make("option", "", choice);
            option.value = choice;
            input.append(option);
          });
        }
        const caption = make("span", "", field.label);
        if (field.required)
          caption.append(make("span", "sw-booking__required", "*"));
        if (field.type === "checkbox") label.append(input, caption);
        else label.append(caption, input);
        if (field.helperText) {
          const helper = make("small", "", field.helperText);
          helper.id = `${id}-helper`;
          input.setAttribute("aria-describedby", helper.id);
          label.append(helper);
        }
        const error = make("small", "sw-booking__field-error");
        error.id = `${id}-error`;
        error.hidden = true;
        label.append(error);
        for (const eventName of ["input", "change"]) {
          input.addEventListener(eventName, () => this.clearFieldError(input), { signal });
        }
        input.addEventListener("blur", () => this.validateField(input), { signal });
        container.append(label);
        if (
          field.type === "discount_code" &&
          !field.required &&
          (presentation !== "classic" || this.element.dataset.refinedLayout === "true")
        ) {
          const disclosure = make("details", "sw-booking__optional");
          disclosure.append(make("summary", "", "Have a referral code?"));
          label.before(disclosure);
          disclosure.append(label);
        }
      });
    }

    clearFieldError(input) {
      input.removeAttribute("aria-invalid");
      const error = input.closest("label")?.querySelector(".sw-booking__field-error");
      if (!error) return;
      error.hidden = true;
      error.textContent = "";
      const describedBy = input.getAttribute("aria-describedby")?.split(" ").filter(id => id !== error.id) || [];
      if (describedBy.length) input.setAttribute("aria-describedby", describedBy.join(" "));
      else input.removeAttribute("aria-describedby");
    }

    showFieldError(input, message) {
      const error = input.closest("label")?.querySelector(".sw-booking__field-error");
      if (!error) return;
      input.closest("details")?.setAttribute("open", "");
      input.setAttribute("aria-invalid", "true");
      error.textContent = message;
      error.hidden = false;
      const describedBy = input.getAttribute("aria-describedby")?.split(" ").filter(Boolean) || [];
      if (!describedBy.includes(error.id)) input.setAttribute("aria-describedby", [...describedBy, error.id].join(" "));
    }

    validateField(input) {
      this.clearFieldError(input);
      if (input.name === "age")
        input.setCustomValidity(input.value && Number(input.value) < 15
          ? "We can only treat patients aged 15 and above." : "");
      let message = "";
      if (input.required && (input.type === "checkbox" ? !input.checked : !input.value.trim()))
        message = input.type === "checkbox" ? "Please check this box to continue." : `Enter your ${input.dataset.fieldLabel.toLowerCase()}.`;
      else if (input.name === "phoneNumber" && input.value &&
        (!PHONE_PATTERN.test(input.value) ||
          input.value.replace(/\D/g, "").length < 10 ||
          input.value.replace(/\D/g, "").length > 15))
        message = "Enter a valid phone number.";
      else if (!input.checkValidity())
        message = input.validity.customError ? input.validationMessage :
          input.type === "email" ? "Enter a valid email address." : `Check your ${input.dataset.fieldLabel.toLowerCase()}.`;
      if (message) this.showFieldError(input, message);
      return !message;
    }

    validateDetails(focus = true) {
      const inputs = this.element.querySelectorAll("[data-fields] input, [data-fields] select, [data-fields] textarea");
      let firstInvalid = null;
      for (const input of inputs) {
        if (!this.validateField(input)) firstInvalid ||= input;
      }
      if (firstInvalid && focus) this.focusFieldError(firstInvalid);
      return !firstInvalid;
    }

    showContactError(code, form) {
      const fields = this.element.querySelector("[data-details]").elements;
      if (code === "CONTACT_INVALID") {
        this.showFieldError(fields.namedItem("email"), "Please check this email address.");
        this.showFieldError(fields.namedItem("phoneNumber"), "Please check this phone number.");
        return true;
      }
      if (code === "REFERRAL_INVALID") {
        const referralField = form?.questions.find(field => field.type === "discount_code");
        const referral = referralField && fields.namedItem(referralField.name);
        if (referral) {
          this.showFieldError(referral, "Check your referral code and try again.");
          return true;
        }
      }
      return false;
    }

    focusFieldError(input = this.element.querySelector('[aria-invalid="true"]')) {
      if (!input) return;
      input.focus({ preventScroll: true });
      input.scrollIntoView({ block: "center", behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth" });
    }

    readDetails(form) {
      // Busy controls are disabled; FormData would omit them during submission.
      const fields = this.element.querySelector("[data-details]").elements;
      const valueOf = (name) => fields.namedItem(name).value.trim();
      const metadata = Object.create(null);
      form.questions.forEach((field) => {
        const value =
          field.type === "checkbox"
            ? fields.namedItem(field.name).checked
            : valueOf(field.name);
        if (field.type !== "discount_code" || value)
          metadata[field.name] = value;
      });
      const tracking = {};
      for (const [key, value] of Object.entries(captureBookingAttribution())) {
        tracking[key] = value;
        if (JSON.stringify({ ...metadata, tracking }).length > MAX_BOOKING_METADATA_LENGTH)
          delete tracking[key];
      }
      if (Object.keys(tracking).length) metadata.tracking = tracking;
      return {
        firstName: valueOf("firstName"),
        lastName: valueOf("lastName"),
        email: valueOf("email").toLowerCase(),
        phoneNumber: valueOf("phoneNumber"),
        metadata,
      };
    }

    renderCalendar(calendar, availableDays, form, selectedDay = null) {
      const month = calendar.month;
      const rolling = Boolean(calendar.windowStart);
      const showDateMonths = rolling && calendar.windowStart.slice(0, 7) !== calendar.windowEnd.slice(0, 7);
      this.element.querySelector("[data-calendar]").hidden = false;
      this.element.querySelector("[data-month]").textContent =
        new Intl.DateTimeFormat("en-GB", { month: "long", year: "numeric", timeZone: "UTC" })
          .format(new Date(`${month}-01T12:00:00Z`));
      if (rolling) {
        const heading = new Intl.DateTimeFormat("en-GB", { month: "short", year: "numeric", timeZone: "UTC" });
        const first = heading.format(new Date(`${calendar.windowStart}T12:00:00Z`));
        const last = heading.format(new Date(`${calendar.windowEnd}T12:00:00Z`));
        this.element.querySelector("[data-month]").textContent = first === last ? first : heading.formatRange(new Date(`${calendar.windowStart}T12:00:00Z`), new Date(`${calendar.windowEnd}T12:00:00Z`));
      }
      this.element.querySelector("[data-timezone]").textContent =
        form.timezone === "Europe/London" ? "Times shown in UK time" : `Times shown in ${form.timezone}`;
      this.element.querySelector("[data-prev-month]").hidden = month === calendar.firstMonth;
      this.element.querySelector("[data-next-month]").hidden = month === calendar.lastMonth;
      if (rolling) {
        const previous = this.element.querySelector("[data-prev-month]");
        const next = this.element.querySelector("[data-next-month]");
        previous.hidden = calendar.windowStart <= calendar.firstWindow;
        next.hidden = calendar.windowEnd >= calendar.lastDay;
        previous.setAttribute("aria-label", "Previous five dates");
        next.setAttribute("aria-label", "Next five dates");
      }
      this.element.querySelector("[data-calendar-empty]").hidden = calendar.loading || calendar.failed || availableDays.length > 0;
      this.element.querySelector("[data-calendar]").setAttribute("aria-busy", String(Boolean(calendar.loading)));
      this.element.querySelector("[data-calendar]").setAttribute("data-availability-state", calendar.loading ? "loading" : calendar.failed ? "error" : "ready");
      const days = this.element.querySelector("[data-days]");
      const weekdays = this.element.querySelector(".sw-booking__weekdays");
      days.replaceChildren();
      weekdays.replaceChildren();
      // Hide weekend columns only when this month's API offers no weekend appointments.
      const hasWeekends = !rolling && availableDays.some(day => [0, 6].includes(new Date(`${day}T12:00:00Z`).getUTCDay()));
      days.classList.toggle("sw-booking__days--weekdays", !hasWeekends);
      weekdays.classList.toggle("sw-booking__weekdays--five", !hasWeekends);
      const labels = rolling
        ? Array.from({ length: CALENDAR_WINDOW_DAYS }, (_, index) => new Intl.DateTimeFormat("en-GB", { weekday: "short", timeZone: "UTC" }).format(new Date(`${addDays(calendar.windowStart, index)}T12:00:00Z`)))
        : hasWeekends ? ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] : ["Mon", "Tue", "Wed", "Thu", "Fri"];
      labels.forEach(label => weekdays.append(make("span", "", label)));
      const monthStart = `${month}-01`;
      const rangeStart = calendar.from || monthStart;
      const start = new Date(`${rangeStart}T12:00:00Z`);
      const monday = calendar.windowStart || addDays(rangeStart, -((start.getUTCDay() + 6) % 7));
      const lastDay = calendar.windowEnd || addDays(`${shiftMonth(month, 1)}-01`, -1);
      for (let iso = monday; iso <= lastDay; iso = addDays(iso, 1)) {
        const weekday = new Date(`${iso}T12:00:00Z`).getUTCDay();
        if (!rolling && !hasWeekends && [0, 6].includes(weekday)) continue;
        if (!rolling && iso < monthStart) { days.append(make("span", "sw-booking__day-padding")); continue; }
        const available = !calendar.loading && availableDays.includes(iso);
        const node = make(available ? "button" : "span", "sw-booking__day");
        const dateText = showDateMonths
          ? new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", timeZone: "UTC" }).format(new Date(`${iso}T12:00:00Z`))
          : String(Number(iso.slice(-2)));
        node.append(make("strong", "", dateText));
        if (available) {
          const earliest = iso === availableDays[0];
          node.append(make("small", "", earliest ? "Earliest" : "Available"));
          node.type = "button";
          node.dataset.day = iso;
          node.setAttribute("aria-pressed", String(iso === selectedDay));
          node.setAttribute("aria-label", `${formatDateLabel(iso)}, ${earliest ? "earliest available in this view" : "available appointments"}`);
        } else node.setAttribute("aria-label", `${formatDateLabel(iso)}, unavailable`);
        days.append(node);
      }
    }

    renderTimes(state) {
      this.element.querySelector("[data-selected-date]").textContent =
        state.selectedDay ? formatDateLabel(state.selectedDay) : "";
      const slots = this.element.querySelector("[data-slots]");
      slots.replaceChildren();
      slots.hidden = false;
      state.slots.forEach((slot) => {
        const time = new Intl.DateTimeFormat("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
          timeZone: state.flow.timezone,
        }).format(slot.startTime);
        const button = make("button", "", time);
        button.type = "button";
        button.dataset.slot = slot.id;
        button.setAttribute(
          "aria-pressed",
          String(slot.id === state.selectedSlot?.id),
        );
        button.setAttribute("aria-label", `${time}, select appointment time`);
        slots.append(button);
      });
      this.element.querySelector("[data-times-empty]").hidden =
        !state.selectedDay || state.slots.length > 0;
      this.element.querySelector("[data-book]").hidden = false;
    }

    renderStep(state, focus = true, previousScroll = null) {
      const appointmentStep = state.step === BOOKING_STEPS.DAY || state.step === BOOKING_STEPS.TIME;
      const directInstant = state.instantEnabled && state.requestedInstant && !appointmentStep;
      const journey = directInstant ? INSTANT_JOURNEY : SCHEDULED_JOURNEY;
      const currentStep = appointmentStep ? JOURNEY_STEPS.APPOINTMENT : state.step === BOOKING_STEPS.COMPLETE ? JOURNEY_STEPS.COMPLETE : JOURNEY_STEPS.DETAILS;
      const totalSteps = journey.length;
      const visibleStep = journey.indexOf(currentStep) + 1;
      const originalDetails = !directInstant && state.presentation === "classic" && state.source?.type === "forms" && state.step === BOOKING_STEPS.DETAILS;
      this.element.querySelector("[data-step-label]").textContent = state.step === BOOKING_STEPS.SELECT
        ? this.element.dataset.groupHeading || "Choose an appointment"
        : `Step ${visibleStep} of ${totalSteps}${originalDetails ? ': Your Details' : ''}`;
      this.element.dataset.step = String(state.step);
      this.element.dataset.appointmentStep = String(appointmentStep);
      this.element.dataset.instantChoice = String(Boolean(state.requestedInstant));
      const heading = this.element.querySelector("[data-appointment-heading]");
      heading.hidden = !appointmentStep;
      if (!heading.dataset.defaultHeading) heading.dataset.defaultHeading = heading.textContent;
      heading.textContent = state.requestedInstant
        ? this.element.dataset.instantSelectedHeading
        : heading.dataset.defaultHeading;
      this.element.querySelector("[data-details-heading]").hidden = state.step !== BOOKING_STEPS.DETAILS || originalDetails;
      const progress = this.element.querySelector('[role="progressbar"]');
      progress.hidden = state.step === BOOKING_STEPS.SELECT;
      progress.setAttribute("aria-valuenow", String(visibleStep));
      progress.setAttribute("aria-valuemax", String(totalSteps));
      [...progress.children].forEach((segment, index) => {
        const stepIndex = journey.indexOf(SCHEDULED_JOURNEY[index]);
        segment.hidden = stepIndex < 0;
        segment.classList.toggle("is-active", stepIndex >= 0 && stepIndex < visibleStep);
        if (!segment.hidden && stepIndex === visibleStep - 1) segment.setAttribute("aria-current", "step");
        else segment.removeAttribute("aria-current");
      });
      const detailsSubmit = this.element.querySelector('[type="submit"]');
      if (!detailsSubmit.dataset.defaultLabel) detailsSubmit.dataset.defaultLabel = detailsSubmit.textContent;
      detailsSubmit.textContent = directInstant
        ? this.element.dataset.instantStartLabel
        : detailsSubmit.dataset.defaultLabel;
      const panels = {
        group: BOOKING_STEPS.SELECT,
        details: BOOKING_STEPS.DETAILS,
        calendar: BOOKING_STEPS.DAY,
        times: BOOKING_STEPS.TIME,
        complete: BOOKING_STEPS.COMPLETE,
      };
      Object.entries(panels).forEach(([name, step]) => {
        this.element.querySelector(`[data-${name}]`).hidden =
          name === "calendar" ? !appointmentStep || !state.calendar :
          name === "times" && state.singleRowCalendar ? !appointmentStep : state.step !== step;
      });
      this.element.querySelector("[data-back]").textContent = "Back to your details";
      this.element.querySelector("[data-back]").hidden = !appointmentStep;
      this.element.querySelector("[data-benefits]").hidden = state.step !== BOOKING_STEPS.DETAILS && this.element.dataset.refinedLayout !== "true";
      const selection = this.element.querySelector("[data-form-selection]");
      selection.hidden = !state.group || !state.flow || state.step === BOOKING_STEPS.COMPLETE || state.step === BOOKING_STEPS.SELECT;
      this.element.querySelector("[data-selected-form]").textContent = state.flow?.name || "";
      this.element.querySelector("[data-change-form]").hidden = !state.group || state.group.forms.length < 2;
      const member = state.group?.forms.find(form => form.slug === state.selectedFormSlug);
      const description = member?.description || (state.flow ? `${state.flow.slotDuration} minute appointment` : "");
      this.element.querySelector("[data-selected-form-description]").textContent = description;
      if (state.step === BOOKING_STEPS.DETAILS && state.flow)
        this.renderIntro(state.flow, state.presentation);
      const entryContextElement = this.element.querySelector('[data-entry-context]');
      if (state.instantEnabled || state.dayTabs) {
        entryContextElement.hidden = state.step !== BOOKING_STEPS.DETAILS || !(state.requestedInstant || state.requestedDay);
        entryContextElement.textContent = state.requestedInstant
          ? this.element.dataset.instantDetailsText
          : state.requestedDay ? formatDateLabel(state.requestedDay) : '';
      }
      if (state.instantEnabled) state.renderInstant();
      if (state.dayTabs) { state.renderDayChoices(); state.publishAvailability(); }
      if (focus) {
        const title = this.element.querySelector(
          appointmentStep ? "[data-appointment-heading]" :
          state.step === BOOKING_STEPS.DETAILS ? originalDetails ? "[data-step-label]" : "[data-details-heading]" :
          state.presentation === "classic" || (state.source?.type === "groups" && state.presentation !== "split")
            ? "[data-title]"
            : "[data-step-label]",
        );
        title.setAttribute("tabindex", "-1");
        title.focus({ preventScroll: true });
        const dialog = this.element.closest("dialog");
        if (dialog) dialog.scrollTop = 0;
        else {
          if (previousScroll)
            window.scrollTo({ ...previousScroll, behavior: "instant" });
          const bounds = title.getBoundingClientRect();
          if (bounds.top < 0 || bounds.bottom > window.innerHeight)
            title.scrollIntoView({ block: "nearest", behavior: "instant" });
        }
      }
    }
  }

  class BookingModal extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.dialog = this.querySelector("dialog");
      const options = { signal: this.controller.signal };
      this.querySelector("[data-open-booking]").addEventListener(
        "click",
        (event) => this.openBooking(event.currentTarget),
        options,
      );
      this.querySelector("[data-close-booking]").addEventListener(
        "click",
        () => this.dialog.close(),
        options,
      );
      this.dialog.addEventListener(
        "close",
        () => {
          this.querySelector("[data-open-booking]").setAttribute(
            "aria-expanded",
            "false",
          );
          this.restoreOpenerFocus();
        },
        options,
      );
      this.dialog.addEventListener(
        "keydown",
        (event) => {
          if (event.key !== "Tab") return;
          const controls = [
            ...this.dialog.querySelectorAll(
              "a[href], button, input, select, textarea, summary, [tabindex]",
            ),
          ].filter(
            (node) =>
              !node.disabled &&
              node.tabIndex >= 0 &&
              node.getClientRects().length,
          );
          const first = controls[0];
          const last = controls.at(-1);
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last?.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first?.focus();
          }
        },
        options,
      );
      // Require both ends of a pointer gesture outside the panel so dragging a
      // selection out of a field never dismisses the customer's booking form.
      const outside = (event) => {
        const rect = this.dialog.getBoundingClientRect();
        return (
          event.target === this.dialog &&
          (event.clientX < rect.left ||
            event.clientX > rect.right ||
            event.clientY < rect.top ||
            event.clientY > rect.bottom)
        );
      };
      this.dialog.addEventListener(
        "pointerdown",
        (event) => {
          this.backdropDown = outside(event);
        },
        options,
      );
      this.dialog.addEventListener(
        "click",
        (event) => {
          if (this.backdropDown && outside(event)) this.dialog.close();
          this.backdropDown = false;
        },
        options,
      );
    }
    restoreOpenerFocus() {
      const inlineChoices = this.opener?.closest('sw-booking-inline');
      const candidates = [
        this.opener,
        inlineChoices?.querySelector('[data-inline-fallback]'),
        ...(inlineChoices?.querySelectorAll('button, a[href]') || []),
        this.querySelector('[data-open-booking]'),
      ];
      let target = candidates.find(node => node?.isConnected && !node.disabled && node.getClientRects().length);
      if (!target && inlineChoices?.isConnected && inlineChoices.getClientRects().length) {
        inlineChoices.setAttribute('tabindex', '-1');
        target = inlineChoices;
      }
      target?.focus({ preventScroll: true });
    }
    openBooking(opener) {
      if (this.dialog.open) return;
      this.opener = opener || this.querySelector("[data-open-booking]");
      this.dialog.showModal();
      this.querySelector("[data-open-booking]").setAttribute(
        "aria-expanded",
        "true",
      );
      // Start at the close control, keeping the heading visible on mobile and
      // avoiding opening the software keyboard before the visitor is ready.
      this.querySelector("[data-close-booking]").focus({ preventScroll: true });
      this.querySelector("sw-booking-flow")?.dispatchEvent(new Event("booking:open"));
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      if (this.dialog?.open) this.dialog.close();
    }
  }
  customElements.define("sw-booking-modal", BookingModal);
  class BookingFlow extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      captureBookingAttribution();
      this.controller = new AbortController();
      this.view = new BookingView(this);
      this.step = BOOKING_STEPS.DETAILS;
      this.presentation = this.dataset.presentation || "classic";
      this.instantEnabled = this.dataset.enableInstant === "true";
      this.dayTabs = this.dataset.dayTabs === "true";
      this.singleRowCalendar = this.dataset.singleRowCalendar === "true" && !this.dayTabs;
      this.inlineAvailabilityState = AVAILABILITY_STATES.LOADING;
      this.showCalendar = false;
      this.instantIntent = false;
      this.requestedInstant = false;
      this.requestedScheduled = false;
      this.requestedSlotId = null;
      this.instantDiscoveryState = AVAILABILITY_STATES.IDLE;
      this.instantRetryAt = 0;
      this.instantSlot = null;
      this.bookingToken = null;
      this.meetingUrl = null;
      const requestedDay = new URL(location.href).searchParams.get("date");
      this.requestedDay =
        this.presentation === "classic" &&
        ISO_DATE_PATTERN.test(requestedDay || "") &&
        Number.isFinite(Date.parse(requestedDay)) &&
        new Date(requestedDay).toISOString().slice(0, 10) === requestedDay
          ? requestedDay
          : null;
      this.selectedDay = null;
      this.selectedSlot = null;
      this.contact = null;
      this.leadAttempt = null;
      this.bookingAttempt = null;
      this.retryAction = null;
      this.busy = false;
      this.cooldown = false;
      this.flow = null;
      this.contactDraft = {};
      this.group = null;
      this.source = null;
      this.selectedFormSlug = null;
      this.resumeAppointmentAfterFormChange = false;
      this.addEventListener(
        "submit",
        (event) => {
          if (event.target !== this.querySelector("[data-details]")) return;
          event.preventDefault();
          if (this.busy || !this.flow || !this.view.validateDetails()) return;
          this.run(() => this.submitDetails(), event.submitter || this.querySelector('[type="submit"]'));
        },
        { signal: this.controller.signal },
      );
      this.addEventListener("click", (event) => this.onClick(event), {
        signal: this.controller.signal,
      });
      this.addEventListener("booking:open", () => {
        if (this.busy || this.cooldown || this.bookingAttempt || this.leadAttempt) return;
        this.run(async () => {
          if (this.flow && this.source?.type === "groups") await this.refreshGroup();
          else if (!this.flow) await this.loadSource();
          await this.selectRequestedClinic();
          if (this.step === BOOKING_STEPS.DETAILS && this.flow) this.view.renderStep(this, false);
          if (this.contact && [BOOKING_STEPS.DAY, BOOKING_STEPS.TIME].includes(this.step)) {
            if (this.requestedInstant) {
              this.step = BOOKING_STEPS.DAY;
              this.view.renderStep(this);
              await this.refreshInstant();
            } else if (this.requestedDay || this.requestedScheduled) await this.showRequestedAppointment();
          }
        });
      }, { signal: this.controller.signal });
      this.run(() => this.loadSource());
      const trustpilot = this.querySelector("[data-trustpilot-widget]");
      if (trustpilot) {
        this.trustpilotObserver = new IntersectionObserver(([entry]) => {
          if (entry.isIntersecting) {
            loadTrustpilot(trustpilot);
            this.trustpilotObserver.disconnect();
          }
        });
        this.trustpilotObserver.observe(trustpilot);
      }
      if (this.dataset.capturePageLinks === "true") {
        this.visibilityObserver = new IntersectionObserver(([entry]) => {
          this.toggleAttribute("data-form-in-view", entry.isIntersecting);
        });
        this.visibilityObserver.observe(
          this.querySelector(".sw-booking__card"),
        );
        document.addEventListener(
          "click",
          (event) => {
            if (
              event.button !== 0 ||
              event.metaKey ||
              event.ctrlKey ||
              event.shiftKey ||
              event.altKey
            )
              return;
            const link = event.target.closest("a[href]");
            if (!link) return;
            let destination;
            try {
              destination = new URL(link.href);
            } catch {
              return;
            }
            const samePageAnchor =
              destination.origin === location.origin &&
              destination.pathname === location.pathname &&
              destination.hash === `#${this.dataset.anchorId}`;
            let bookingLink = this.dayTabs && destination.origin === location.origin &&
              destination.pathname.replace(/\/$/, '') === '/pages/booking';
            try {
              const target = resolveBookingSource(destination.href);
              bookingLink = bookingLink || this.source && target.type === this.source.type && target.slug === this.source.slug;
            } catch { /* Unrelated page links are not booking sources. */ }
            if (!samePageAnchor && !bookingLink) return;
            event.preventDefault();
            event.stopImmediatePropagation();
            this.requestedClinicName = link.dataset.bookingClinicName || null;
            const disclosure = this.closest(".booking-experience__reveal");
            if (disclosure) disclosure.open = true;
            this.closest("sw-booking-modal")?.openBooking(link);
            requestAnimationFrame(() => {
              const focusTarget =
                this.querySelector("input:not([hidden])") || this;
              if (this.step === BOOKING_STEPS.DETAILS)
                focusTarget.focus({ preventScroll: true });
              if (!this.closest("dialog")) this.scrollIntoView({ block: "start", behavior: "auto" });
            });
          },
          { capture: true, signal: this.controller.signal },
        );
      }
    }
    disconnectedCallback() {
      this.visibilityObserver?.disconnect();
      this.trustpilotObserver?.disconnect();
      this.removeAttribute("data-form-in-view");
      this.controller?.abort();
      this.formController?.abort();
      clearTimeout(this.cooldownTimer);
      clearTimeout(this.instantCooldownTimer);
      clearTimeout(this.meetingTimer);
      this.bookingToken = null;
      this.meetingUrl = null;
      this.controller = null;
      this.contact = null;
      this.leadAttempt = null;
      this.bookingAttempt = null;
    }

    async run(action, control = null) {
      if (this.busy || !this.controller) return;
      const controller = this.controller;
      let focusFieldError = false;
      this.busy = true;
      this.busyControl = control || (this.dayTabs && !this.flow
        ? this.querySelector('[type="submit"]')
        : null);
      this.retryAction = null;
      this.querySelector("[data-error]").hidden = true;
      this.querySelector("[data-retry]").hidden = true;
      this.view.updateControls(this);
      try {
        await action();
      } catch (error) {
        if (this.controller !== controller || controller.signal.aborted) return;
        if (this.dayTabs && this.inlineAvailabilityState === AVAILABILITY_STATES.LOADING) {
          this.inlineAvailabilityState = AVAILABILITY_STATES.ERROR;
          this.publishAvailability();
        }
        focusFieldError = this.step === BOOKING_STEPS.DETAILS && this.view.showContactError(error.code, this.flow);
        if (!focusFieldError)
          this.view.showError(messages[error.code] || "Booking is currently unavailable. Please try again later.");
        this.retryAction = focusFieldError || error.noRetry ? null : error.recovery || action;
        this.querySelector("[data-retry]").hidden = !this.retryAction;
        if (["RATE_LIMITED", "SERVICE_BUSY"].includes(error.code) && !error.instantDiscovery) {
          this.startCooldown(error);
        }
      } finally {
        if (this.controller === controller) {
          this.busy = false;
          this.busyControl = null;
          this.removeAttribute("data-loading");
          this.view.updateControls(this);
          if (this.dayTabs) this.publishAvailability();
          if (focusFieldError) this.view.focusFieldError();
        }
      }
    }

    async loadSource() {
      this.source = resolveBookingSource(this.dataset.bookingUrl, this.dataset.formSlug);
      this.dataset.sourceType = this.source.type;
      if (this.source.type === "groups") {
        this.step = BOOKING_STEPS.SELECT;
        this.view.renderStep(this, false);
        if (this.closest("sw-booking-modal") && !this.closest("sw-booking-modal").querySelector("dialog").open) return;
        await this.loadGroup();
      } else await this.loadForm(this.source.slug);
    }

    async readGroup() {
      const client = new BookingApiClient(this.dataset.apiBase, this.source.slug, this.controller.signal, "groups");
      const group = validateGroupConfiguration(await client.request(""));
      if (group.slug !== this.source.slug) throw Error("Invalid group response");
      return group;
    }

    async loadGroup(focus = false) {
      const name = this.group?.name || "Appointments";
      this.group = null;
      this.view.renderGroup({ name, forms: [] });
      this.querySelector("[data-group-empty]").hidden = true;
      const group = await this.readGroup();
      if (!this.controller || this.controller.signal.aborted) return;
      this.group = group;
      this.view.renderGroup(group);
      if (group.forms.length === 1) await this.loadForm(group.forms[0].slug);
      else this.view.renderStep(this, focus);
    }

    async loadForm(slug = this.dataset.formSlug, focus = false, renderDetails = true) {
      clearTimeout(this.instantCooldownTimer);
      this.instantRetryAt = 0;
      if (this.dayTabs) {
        this.inlineAvailabilityState = AVAILABILITY_STATES.LOADING;
        this.publishAvailability();
      }
      this.destination = getCompletionUrl(this.dataset.completionPath);
      this.formController?.abort();
      this.formController = new AbortController();
      const signal = AbortSignal.any([this.controller.signal, this.formController.signal]);
      const api = new BookingApiClient(
        this.dataset.apiBase,
        slug,
        signal,
      );
      this.api = api;
      const form = await api.getForm();
      if (this.api !== api || signal.aborted) return;
      // Capture after the request, before replacing the focused clinic button.
      const previousScroll = focus && !this.closest("dialog")
        ? { top: window.scrollY, left: window.scrollX }
        : null;
      this.selectedFormSlug = slug;
      this.view.renderFields(form, this.presentation, signal);
      for (const field of CONTACT_FIELDS) {
        const input = this.querySelector("[data-details]").elements.namedItem(field.name);
        if (this.contactDraft?.[field.name] !== undefined) input.value = this.contactDraft[field.name];
      }
      this.flow = form;
      this.step = BOOKING_STEPS.DETAILS;
      this.view.renderFacts(this.flow);
      if (renderDetails) this.view.renderStep(this, focus, previousScroll);
      if (this.dayTabs) await this.loadInlineAvailability();
      else if (this.instantEnabled) await this.refreshInstant();
    }

    async loadInlineAvailability() {
      const api = this.api;
      const signal = this.formController.signal;
      const today = dateKey(new Date(), this.flow.timezone);
      const lastDay = addDays(today, this.flow.daysIntoFuture);
      const lookahead = addDays(today, INLINE_DAY_LOOKAHEAD_DAYS);
      const to = lookahead < lastDay ? lookahead : lastDay;
      this.inlineAvailabilityState = AVAILABILITY_STATES.LOADING;
      this.inlineAvailabilityFrom = today;
      this.inlineAvailabilityTo = to;
      this.inlineDays = [];
      this.publishAvailability();
      let availabilityFailed = false;
      const [todaySlots] = await Promise.all([
        this.refreshInstant({ includeScheduled: true }),
        api.getAvailability(today, to).then(dates => {
          if (this.api !== api || signal.aborted) return;
          this.inlineDays = dates.filter(day => day.hasAvailability).map(day => day.date);
          if (!this.calendar) this.days = this.inlineDays;
        }).catch(() => { availabilityFailed = true; }),
      ]);
      if (this.api !== api || signal.aborted) return;
      // Calendar dates include ongoing instant-only slots. Today's scheduled
      // choice requires a future slot from the same discovery used for instant.
      if (!todaySlots?.slots.some(slot => slot.scheduledBookingEligible))
        this.inlineDays = this.inlineDays.filter(day => day !== today);
      if (!this.calendar) this.days = this.inlineDays;
      this.inlineAvailabilityState = availabilityFailed ? AVAILABILITY_STATES.ERROR : AVAILABILITY_STATES.READY;
      this.publishAvailability();
    }

    resetFormSelection() {
      clearTimeout(this.instantCooldownTimer);
      this.instantRetryAt = 0;
      const fields = this.querySelector("[data-details]").elements;
      this.contactDraft = Object.fromEntries(CONTACT_FIELDS.map(field => [field.name, fields.namedItem(field.name)?.value || ""]));
      this.formController?.abort();
      this.api = null;
      this.flow = null;
      this.contact = null;
      this.selectedFormSlug = null;
      this.instantIntent = false;
      this.requestedInstant = false;
      this.requestedScheduled = false;
      this.requestedSlotId = null;
      this.instantDiscoveryState = AVAILABILITY_STATES.IDLE;
      this.instantSlot = null;
      this.resumeAppointmentAfterFormChange = false;
      this.selectedDay = null;
      this.selectedSlot = null;
      this.calendar = null;
      this.days = [];
      this.slots = [];
      this.requestedDay = null;
      this.updateDateLink(null);
      this.querySelector("[data-fields]").replaceChildren();
      this.step = BOOKING_STEPS.SELECT;
    }

    async refreshGroup() {
      const name = this.group?.name || "Appointments";
      try {
        const group = await this.readGroup();
        if (!group.forms.some(form => form.slug === this.selectedFormSlug)) {
          this.resetFormSelection();
          this.group = group;
          this.view.renderGroup(group);
          if (group.forms.length === 1) await this.loadForm(group.forms[0].slug, true);
          else this.view.renderStep(this);
        } else {
          this.group = group;
          this.view.renderStep(this, false);
        }
      } catch (error) {
        if (!this.controller || this.controller.signal.aborted) return;
        this.resetFormSelection();
        this.group = null;
        this.view.renderGroup({ name, forms: [] });
        this.view.renderStep(this);
        error.recovery = () => this.loadGroup(true);
        throw error;
      }
    }

    changeForm() {
      if (!this.group || this.busy || this.cooldown || this.bookingAttempt || this.leadAttempt) return;
      const resumeAppointment = [BOOKING_STEPS.DAY, BOOKING_STEPS.TIME].includes(this.step);
      this.resetFormSelection();
      this.resumeAppointmentAfterFormChange = resumeAppointment;
      this.view.renderGroup(this.group);
      this.view.renderStep(this);
      return this.run(() => this.loadGroup(true));
    }

    async selectForm(slug) {
      const resumeAppointment = this.resumeAppointmentAfterFormChange;
      await this.loadForm(slug, !resumeAppointment, !resumeAppointment);
      if (this.selectedFormSlug !== slug) return;
      this.resumeAppointmentAfterFormChange = false;
      if (!resumeAppointment) return;
      if (!this.view.validateDetails(false)) {
        this.view.renderStep(this);
        this.view.focusFieldError();
        return;
      }
      try {
        await this.submitDetails();
      } catch (error) {
        if (this.step === BOOKING_STEPS.DETAILS) this.view.renderStep(this);
        if (!error.recovery && !error.noRetry) error.recovery = () => this.submitDetails();
        throw error;
      }
    }

    async selectRequestedClinic() {
      const requested = this.requestedClinicName;
      if (!requested || this.source?.type !== "groups" || !this.group) return;
      const normalize = name => name.trim().replace(/\s+/g, " ").toLocaleLowerCase();
      const matches = this.group.forms.filter(form => normalize(form.name) === normalize(requested));
      if (matches.length !== 1) {
        if (this.flow) {
          this.resetFormSelection();
          this.view.renderGroup(this.group);
          this.view.renderStep(this);
        }
        this.requestedClinicName = null;
        return;
      }
      const slug = matches[0].slug;
      if (this.selectedFormSlug === slug) {
        this.requestedClinicName = null;
        return;
      }
      if (this.flow) {
        const resumeAppointment = [BOOKING_STEPS.DAY, BOOKING_STEPS.TIME].includes(this.step);
        this.resetFormSelection();
        this.resumeAppointmentAfterFormChange = resumeAppointment;
      }
      await this.selectForm(slug);
      this.requestedClinicName = null;
    }

    async submitDetails() {
      if (this.bookingAttempt) return this.book();
      if (!this.leadAttempt) {
        const details = this.view.readDetails(this.flow);
        this.contact = await this.api.validateDetails(details, this.flow);
        this.leadAttempt = {
          key: createIdempotencyKey(),
          payload: this.contact.payload,
        };
      }
      try {
        const result = await this.api.request(
          "/leads",
          this.leadAttempt.payload,
          this.leadAttempt.key,
        );
        if (!result.accepted) throw Error("Invalid lead response");
      } catch (error) {
        // A lost response must replay the exact payload/key before editing details.
        if (
          error.code &&
          !["SERVICE_BUSY", "RATE_LIMITED"].includes(error.code)
        )
          this.leadAttempt = null;
        throw error;
      }
      this.leadAttempt = null;
      this.instantIntent = false;
      if (this.instantEnabled && this.requestedInstant) {
        // The details submit explicitly starts the call; do not ask for a time again.
        try {
          await this.chooseInstant();
        } catch (error) {
          if (!this.instantIntent) {
            this.step = BOOKING_STEPS.DAY;
            this.view.renderStep(this);
          }
          // Retry the call using the captured contact and original booking key.
          if (!error.noRetry) error.recovery = () => this.chooseInstant();
          throw error;
        }
        return;
      }
      this.step = BOOKING_STEPS.DAY;
      this.view.renderStep(this);
      // Scheduled customers must not wait for the optional instant lookup.
      if (this.instantEnabled) void this.refreshInstant();
      if (this.requestedInstant) return;
      await this.showRequestedAppointment();
    }

    async showRequestedAppointment() {
      // A different invitation must never leave the previous time bookable.
      this.selectedSlot = null;
      this.selectedDay = null;
      this.slots = [];
      this.step = BOOKING_STEPS.DAY;
      this.view.renderStep(this, false);
      if (this.singleRowCalendar) this.view.renderTimes(this);
      try {
        const requestedDay = this.requestedDay;
        const requestedSlotId = this.requestedSlotId;
        const today = dateKey(new Date(), this.flow.timezone);
        const lastDay = addDays(today, this.flow.daysIntoFuture);
        const requestedMonth =
          requestedDay && requestedDay >= today && requestedDay <= lastDay
            ? requestedDay.slice(0, 7)
            : null;
        await this.loadAvailability(this.singleRowCalendar ? (requestedMonth ? requestedDay : null) : requestedMonth);
        if (requestedDay && this.days.includes(requestedDay)) {
          await this.loadSlots(requestedDay);
          if (requestedSlotId) {
            this.selectedSlot = this.slots.find(slot => slot.id === requestedSlotId) || null;
            this.view.renderTimes(this);
            this.view.updateControls(this);
            if (!this.selectedSlot) this.view.showError(messages.SLOT_UNAVAILABLE);
          }
        } else if (this.singleRowCalendar && !requestedDay) await this.openFirstAvailableDay(true);
        else if (this.dayTabs && !requestedDay && this.days.length) await this.loadSlots(this.days[0]);
        else if (requestedDay) {
          this.updateDateLink(null);
          this.view.showError("That day is no longer available. Please choose another day.");
        }
        this.requestedDay = null;
        this.requestedScheduled = false;
        this.requestedSlotId = null;
      } catch (error) {
        error.recovery = () => this.showRequestedAppointment();
        throw error;
      }
    }

    async openFirstAvailableDay(searchLaterWindows = false) {
      do {
        while (this.days.length) {
          const day = this.days[0];
          // The first visible date is the next bookable day, not the start of its week.
          if (this.calendar.windowStart !== day) await this.loadAvailability(day);
          await this.loadSlots(day);
          if (this.slots.length) return;
          this.days = this.days.filter(availableDay => availableDay !== day);
          this.view.renderCalendar(this.calendar, this.days, this.flow, this.selectedDay);
        }
        if (!searchLaterWindows || this.calendar.windowEnd >= this.calendar.lastDay) return;
        await this.loadAvailability(addDays(this.calendar.windowStart, CALENDAR_WINDOW_DAYS));
      } while (true);
    }

    async loadAvailability(month = null) {
      const today = dateKey(new Date(), this.flow.timezone);
      const lastDay = addDays(today, this.flow.daysIntoFuture);
      if (this.singleRowCalendar) {
        const firstWindow = today;
        const anchor = month || this.calendar?.windowStart || today;
        const windowStart = anchor.length === 7 ? `${anchor}-01` : anchor;
        const windowEnd = addDays(windowStart, CALENDAR_WINDOW_DAYS - 1);
        const from = windowStart < today ? today : windowStart;
        const to = windowEnd > lastDay ? lastDay : windowEnd;
        if (from > to || windowStart < firstWindow) throw Error("Invalid calendar range");
        const calendar = { month: from.slice(0, 7), windowStart, windowEnd, firstWindow, lastDay, from, to };
        this.calendar = { ...calendar, loading: true };
        this.days = [];
        this.selectedDay = null;
        this.selectedSlot = null;
        this.slots = [];
        this.view.renderTimes(this);
        this.view.renderCalendar(this.calendar, [], this.flow);
        this.view.renderStep(this, false);
        let dates;
        try {
          dates = await this.api.getAvailability(from, to);
        } catch (error) {
          this.calendar = { ...calendar, failed: true };
          this.view.renderCalendar(this.calendar, [], this.flow);
          throw error;
        }
        this.days = dates.filter(day => day.hasAvailability && day.date >= from && day.date <= to)
          .map(day => day.date).sort();
        this.calendar = calendar;
        this.view.renderCalendar(calendar, this.days, this.flow, this.selectedDay);
        return;
      }
      const requestedMonth =
        month ||
        this.calendar?.month ||
        this.selectedDay?.slice(0, 7) ||
        today.slice(0, 7);
      const firstMonth = today.slice(0, 7);
      const lastMonth = lastDay.slice(0, 7);
      if (requestedMonth < firstMonth || requestedMonth > lastMonth)
        throw Error("Invalid calendar month");
      const range = getMonthRange(requestedMonth, today, lastDay);
      const dates = await this.api.getAvailability(range.from, range.to);
      this.calendar = { month: requestedMonth, firstMonth, lastMonth, from: range.from };
      this.days = dates
        .filter((day) => day.hasAvailability)
        .map((day) => day.date).sort();
      this.view.renderCalendar(this.calendar, this.days, this.flow, this.selectedDay);
      if (this.dayTabs) { this.renderDayChoices(); this.publishAvailability(); }
    }

    async changeMonth(direction) {
      if (this.singleRowCalendar) {
        const candidate = addDays(this.calendar.windowStart, direction * CALENDAR_WINDOW_DAYS);
        const start = candidate < this.calendar.firstWindow ? this.calendar.firstWindow : candidate;
        this.selectedDay = null;
        this.selectedSlot = null;
        this.slots = [];
        this.step = BOOKING_STEPS.DAY;
        this.updateDateLink(null);
        this.view.renderTimes(this);
        const loadWindow = async () => {
          await this.loadAvailability(start);
          await this.openFirstAvailableDay();
        };
        try { await loadWindow(); }
        catch (error) { error.recovery = loadWindow; throw error; }
        return;
      }
      const month = shiftMonth(this.calendar.month, direction);
      await this.loadAvailability(month);
      this.selectedDay = null;
      this.selectedSlot = null;
      this.slots = [];
      this.step = BOOKING_STEPS.DAY;
      this.updateDateLink(null);
      this.view.renderStep(this, false);
    }

    updateDateLink(day) {
      if (this.presentation !== "classic") return;
      const url = new URL(location.href);
      if (day) url.searchParams.set("date", day);
      else url.searchParams.delete("date");
      history.replaceState(null, "", url.href);
    }

    async loadSlots(day) {
      const focusedDay = this.singleRowCalendar && document.activeElement?.dataset.day;
      this.instantIntent = false;
      this.requestedInstant = false;
      const result = await this.api.request(`/slots?date=${day}`);
      // This section offers scheduled booking; never silently opt into an instant booking.
      this.slots = result.slots.filter((slot) => slot.scheduledBookingEligible);
      this.selectedDay = day;
      this.selectedSlot = null;
      this.updateDateLink(day);
      this.step = BOOKING_STEPS.TIME;
      this.view.renderCalendar(this.calendar, this.days, this.flow, day);
      this.view.renderStep(this, false);
      this.view.renderTimes(this);
      if (this.singleRowCalendar) {
        if (focusedDay) this.querySelector(`[data-day="${day}"]`)?.focus({ preventScroll: true });
        return;
      }
      const heading = this.querySelector("[data-selected-date]");
      heading.setAttribute("tabindex", "-1");
      heading.focus({ preventScroll: true });
      heading.scrollIntoView({ block: "nearest", behavior: "auto" });
    }

    publishAvailability() {
      this.dispatchEvent(new Event('booking:availability', { bubbles: true }));
    }

    renderDayChoices() {
      const choices = this.querySelector('[data-day-choices]');
      const appointment = !this.requestedInstant && [BOOKING_STEPS.DAY, BOOKING_STEPS.TIME].includes(this.step);
      choices.hidden = !appointment;
      choices.replaceChildren();
      if (!this.flow) return;
      const today = dateKey(new Date(), this.flow.timezone);
      for (const day of (this.days || []).slice(0, INLINE_DAY_CHOICE_COUNT)) {
        const button = make('button', '');
        button.type = 'button';
        button.dataset.day = day;
        const label = day === today ? 'today' : day === addDays(today, 1) ? 'tomorrow' : formatDateLabel(day);
        button.append(make('strong', '', `${this.dataset.dayPrefix} ${label}`));
        button.append(make('span', '', new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(new Date(`${day}T12:00:00Z`))));
        button.setAttribute('aria-pressed', String(day === this.selectedDay));
        button.disabled = Boolean(this.busy || this.bookingAttempt);
        choices.append(button);
      }
      const more = make('button', '', this.dataset.moreDatesLabel);
      more.type = 'button';
      more.dataset.toggleCalendar = '';
      more.setAttribute('aria-expanded', String(this.showCalendar));
      more.disabled = Boolean(this.busy || this.bookingAttempt);
      choices.append(more);
      this.querySelector('[data-calendar]').hidden = !appointment || !this.showCalendar || !this.calendar;
    }

    openInvitation(opener, day, slotId = null) {
      if (this.busy || this.cooldown || this.bookingAttempt || this.leadAttempt) return;
      if (!this.bookingToken) {
        const primary = Boolean(opener?.hasAttribute?.('data-inline-instant'));
        const scheduled = primary && opener?.dataset?.inlineMode === BOOKING_CHOICES.SCHEDULED;
        const instant = primary && !scheduled;
        if (instant && this.instantEnabled) {
          this.requestedInstant = true;
          this.requestedScheduled = false;
          this.requestedDay = null;
          this.requestedSlotId = null;
        } else if (scheduled) {
          this.requestedInstant = false;
          this.requestedScheduled = true;
          this.requestedDay = null;
          this.requestedSlotId = null;
        } else if (day && (this.inlineDays?.includes(day) || this.days?.includes(day))) {
          this.requestedInstant = false;
          this.requestedScheduled = false;
          this.requestedDay = day;
          this.requestedSlotId = this.slots?.some(slot => slot.id === slotId && slot.scheduledBookingEligible) ? slotId : null;
        }
      }
      this.closest('sw-booking-modal')?.openBooking(opener);
      // External choices preserve intent; only explicit confirmation submits a booking.
    }

    renderInstant() {
      const panel = this.querySelector('[data-instant-panel]');
      if (!panel) return;
      const appointment = [BOOKING_STEPS.DAY, BOOKING_STEPS.TIME].includes(this.step);
      panel.hidden = !this.instantEnabled || !this.contact || !appointment ||
        (!this.requestedInstant && !this.instantSlot);
      const label = panel.querySelector('[data-instant-action-label]');
      if (label) label.textContent = this.instantDiscoveryState === AVAILABILITY_STATES.ERROR
        ? this.dataset.instantRetryLabel
        : this.requestedInstant
        ? this.dataset.instantStartLabel
        : this.dataset.instantButtonLabel;
      const action = panel.querySelector('[data-choose-instant]');
      if (action) {
        action.disabled = instantActionDisabled(this);
        action.setAttribute('aria-busy', String(this.instantDiscoveryState === AVAILABILITY_STATES.LOADING));
      }
      const status = panel.querySelector('[data-instant-status]');
      if (status) {
        if (!status.dataset.availableText) status.dataset.availableText = status.textContent;
        status.textContent = this.instantDiscoveryState === AVAILABILITY_STATES.ERROR
          ? this.dataset.instantCheckError
          : this.instantSlot || this.instantDiscoveryState === AVAILABILITY_STATES.LOADING ? status.dataset.availableText
          : this.dataset.instantUnavailableText;
        // Do not claim availability until the current check has succeeded.
        status.style.visibility = this.instantDiscoveryState === AVAILABILITY_STATES.LOADING ? 'hidden' : '';
        status.dataset.discoveryState = this.instantDiscoveryState;
      }
      const scheduled = panel.querySelector('[data-choose-scheduled]');
      if (scheduled) scheduled.hidden = !this.requestedInstant;
      if (this.requestedInstant && appointment) {
        for (const selector of ['[data-calendar]', '[data-times]', '[data-day-choices]']) {
          const node = this.querySelector(selector);
          if (node) node.hidden = true;
        }
      }
    }

    async refreshInstant({ includeScheduled = false } = {}) {
      if (this.instantRetryAt > Date.now()) return null;
      const api = this.api;
      const signal = this.formController?.signal;
      const generation = this.instantDiscoveryGeneration = (this.instantDiscoveryGeneration || 0) + 1;
      const current = () => this.api === api && !signal?.aborted && this.instantDiscoveryGeneration === generation;
      this.instantSlot = null;
      this.instantDiscoveryState = AVAILABILITY_STATES.LOADING;
      this.instantDiscoveryError = null;
      this.renderInstant();
      let result = null;
      try {
        if (includeScheduled || this.instantEnabled && this.flow?.bookingType === 'consultation') {
          result = await api.request(`/slots?date=${dateKey(new Date(), this.flow.timezone)}`);
          if (!current()) return null;
          if (this.instantEnabled && this.flow?.bookingType === 'consultation')
            this.instantSlot = result.slots.find(slot => slot.instantBookingEligible) || null;
        }
        if (!current()) return null;
        this.instantDiscoveryState = AVAILABILITY_STATES.READY;
      } catch (error) {
        if (!current()) return null;
        this.instantDiscoveryState = AVAILABILITY_STATES.ERROR;
        this.instantDiscoveryError = error;
        this.instantSlot = null;
        if (['RATE_LIMITED', 'SERVICE_BUSY'].includes(error.code)) {
          error.instantDiscovery = true;
          this.startInstantCooldown(error);
        }
      }
      this.renderInstant();
      if (this.dayTabs) this.publishAvailability();
      return result;
    }

    startInstantCooldown(error) {
      const retryDelay = error.retryAfter * MILLISECONDS_PER_SECOND;
      this.instantRetryAt = Date.now() + retryDelay;
      clearTimeout(this.instantCooldownTimer);
      this.instantCooldownTimer = setTimeout(() => {
        this.instantRetryAt = 0;
        this.renderInstant();
        this.view.updateControls(this);
        if (this.dayTabs) this.publishAvailability();
      }, retryDelay);
    }

    startCooldown(error) {
      this.cooldown = true;
      clearTimeout(this.cooldownTimer);
      this.cooldownTimer = setTimeout(() => {
        this.cooldown = false;
        this.view.updateControls(this);
        if (this.dayTabs) this.publishAvailability();
      }, error.retryAfter * MILLISECONDS_PER_SECOND);
      this.view.updateControls(this);
    }

    async chooseInstant() {
      if (!this.instantEnabled || this.bookingToken) return;
      // A lost response must replay the original request before checking fresh capacity.
      if (this.bookingAttempt) return this.book();
      await this.refreshInstant();
      if (this.instantDiscoveryState === AVAILABILITY_STATES.ERROR) throw this.instantDiscoveryError;
      if (!this.instantSlot) {
        this.instantIntent = false;
        const error = Error('Instant availability changed');
        error.code = 'SLOT_UNAVAILABLE';
        throw error;
      }
      const detailsConfirmation = this.requestedInstant && this.step === BOOKING_STEPS.DETAILS;
      if (!this.contact || (!detailsConfirmation && ![BOOKING_STEPS.DAY, BOOKING_STEPS.TIME].includes(this.step))) return;
      this.instantIntent = true;
      this.selectedSlot = this.instantSlot;
      await this.book();
    }

    startCallConnection() {
      clearTimeout(this.meetingTimer);
      this.meetingUrl = null;
      const token = this.bookingToken;
      const client = this.api;
      const panel = this.querySelector('[data-call-connection]');
      const heading = panel.querySelector('[data-call-heading]');
      const status = panel.querySelector('[data-call-status]');
      const join = panel.querySelector('[data-join-call]');
      const retry = panel.querySelector('[data-retry-call]');
      panel.querySelector('[data-call-received]').textContent = window.SmileWhiteBookingCallCopy.received;
      panel.querySelector('[data-call-join-label]').textContent = window.SmileWhiteBookingCallCopy.join;
      panel.hidden = false;
      this.querySelector('[data-complete]').hidden = true;
      heading.textContent = this.dataset.callWaitingHeading;
      const copy = panel.querySelector('[data-call-copy]');
      const spinner = panel.querySelector('[data-call-spinner]');
      const meetingStep = panel.querySelector('[data-call-meeting-step]');
      const joinStep = panel.querySelector('[data-call-join-step]');
      const meetingLabel = panel.querySelector('[data-call-meeting-label]');
      if (!meetingLabel.dataset.waitingLabel) meetingLabel.dataset.waitingLabel = window.SmileWhiteBookingCallCopy.meetingWaiting;
      if (!status.dataset.preparingLabel) status.dataset.preparingLabel = window.SmileWhiteBookingCallCopy.preparing;
      copy.textContent = this.dataset.callWaitingCopy;
      spinner.hidden = false;
      meetingStep.className = 'is-current';
      joinStep.className = '';
      meetingLabel.textContent = meetingLabel.dataset.waitingLabel;
      const email = panel.querySelector('[data-call-email]');
      email.textContent = this.callSummary?.email || '';
      email.hidden = !this.callSummary?.email;
      panel.querySelector('[data-call-duration]').textContent = this.callSummary?.duration
        ? window.SmileWhiteBookingCallCopy.duration.replace('{minutes}', this.callSummary.duration) : window.SmileWhiteBookingCallCopy.durationUnknown;
      status.textContent = status.dataset.preparingLabel;
      join.hidden = true; retry.hidden = true;
      heading.focus({ preventScroll: true });
      let attempts = 0;
      const started = Date.now();
      const poll = async () => {
        try {
          const current = await client.request('', undefined, undefined, token);
          if (!this.controller || this.controller.signal.aborted || this.bookingToken !== token) return;
          if (current.meetingUrl) {
            const url = new URL(current.meetingUrl);
            if (url.protocol !== 'https:' || url.username || url.password) throw Error('Invalid meeting URL');
            this.meetingUrl = url.href;
            heading.textContent = this.dataset.instantReadyHeading;
            status.textContent = this.dataset.callStatusReady || window.SmileWhiteBookingCallCopy.ready;
            copy.textContent = this.dataset.callReadyCopy;
            spinner.hidden = true;
            meetingStep.className = 'is-done';
            joinStep.className = 'is-current';
            meetingLabel.textContent = this.dataset.callMeetingReady || window.SmileWhiteBookingCallCopy.meetingReady;
            join.hidden = false;
            return;
          }
          attempts++;
          if (attempts >= MEETING_POLL_LIMIT) throw Error('Meeting unavailable');
          if (Date.now() - started >= MEETING_WAIT_NOTICE_MS) {
            heading.textContent = this.dataset.callDelayedHeading;
            copy.textContent = this.dataset.callDelayedCopy;
            status.textContent = this.dataset.callStatusDelayed || window.SmileWhiteBookingCallCopy.delayed;
          }
          const pollDelay = Math.min(MEETING_POLL_MAX_MS, MEETING_POLL_INITIAL_MS * MEETING_POLL_BACKOFF_FACTOR ** attempts);
          this.meetingTimer = setTimeout(poll, pollDelay);
        } catch {
          if (!this.controller || this.bookingToken !== token) return;
          heading.textContent = this.dataset.callErrorHeading;
          status.textContent = this.dataset.callStatusError || window.SmileWhiteBookingCallCopy.error;
          copy.textContent = this.dataset.callErrorCopy;
          spinner.hidden = true;
          meetingStep.className = '';
          retry.hidden = false;
        }
      };
      poll();
    }

    async recoverBookingFailure(error) {
      if (["SLOT_FULL", "SLOT_UNAVAILABLE"].includes(error.code)) {
        this.bookingAttempt = null;
        if (this.instantIntent) {
          this.instantIntent = false;
          await this.refreshInstant();
          this.step = BOOKING_STEPS.DAY;
          await this.loadAvailability();
          this.view.renderStep(this);
        } else await this.loadSlots(this.selectedDay);
        error.noRetry = true;
      } else if (
        ["VALIDATION_REQUIRED", "CONTACT_INVALID", "REFERRAL_INVALID"].includes(
          error.code,
        )
      ) {
        this.bookingAttempt = null;
        this.step = BOOKING_STEPS.DETAILS;
        this.view.renderStep(this);
        error.noRetry = true;
      } else if (
        error.code &&
        !["SERVICE_BUSY", "RATE_LIMITED"].includes(error.code)
      ) {
        this.bookingAttempt = null;
        if (error.code === "BOOKING_EXISTS") error.noRetry = true;
      }
    }

    async book() {
      if (!this.selectedSlot || !this.contact) return;
      if (!this.bookingAttempt) {
        if (this.contact.expiry <= Date.now() + CONTACT_REFRESH_BUFFER_MS)
          this.contact = await this.api.validateDetails(
            this.contact.payload,
            this.flow,
          );
        this.bookingAttempt = {
          key: createIdempotencyKey(),
          payload: { ...this.contact.payload, slotId: this.selectedSlot.id, ...(this.instantIntent && this.instantEnabled ? { isInstantBooking: true } : {}) },
        };
      }
      let result;
      try {
        result = await this.api.request(
          "/bookings",
          this.bookingAttempt.payload,
          this.bookingAttempt.key,
        );
      } catch (error) {
        await this.recoverBookingFailure(error);
        throw error;
      }
      if (
        result.status !== (this.bookingAttempt.payload.isInstantBooking ? BOOKING_STATUSES.INSTANT : BOOKING_STATUSES.SCHEDULED) ||
        !Number.isFinite(result.scheduledTime) ||
        !result.token
      )
        throw Error("Invalid booking response");
      if (result.status === BOOKING_STATUSES.INSTANT) {
        trackInstantBooking(result.token);
        this.callSummary = {
          email: this.contact.payload.email,
          duration: Math.round((this.selectedSlot.endTime - this.selectedSlot.startTime) / MILLISECONDS_PER_MINUTE),
        };
        this.bookingToken = result.token;
        this.bookingAttempt = null;
        this.contact = null;
        this.step = BOOKING_STEPS.COMPLETE;
        this.view.renderStep(this, false);
        this.startCallConnection();
        return;
      }
      // Confirmation capability stays out of URLs, storage, third-party scripts and analytics.
      // The configured Shopify thank-you page does not require it.
      this.contact = null;
      this.step = BOOKING_STEPS.COMPLETE;
      this.view.renderStep(this);
      const destination = window.SmileWhiteBookingConfirmation.save(
        this.destination,
        {
          start: result.scheduledTime,
          end:
            result.scheduledTime +
            (this.selectedSlot.endTime - this.selectedSlot.startTime),
          timezone: result.form.timezone,
          address: result.address,
          title: this.flow.name,
        },
      );
      location.assign(destination);
    }

    onClick(event) {
      const button = event.target.closest("button");
      if (!button || !this.contains(button) || this.busy || this.cooldown)
        return;
      if (button.hasAttribute('data-toggle-calendar') && this.dayTabs && !this.bookingAttempt) {
        this.showCalendar = !this.showCalendar;
        this.renderDayChoices();
        this.querySelector('[data-toggle-calendar]')?.focus({ preventScroll: true });
        return;
      }
      if (button.hasAttribute('data-choose-scheduled')) return this.run(async () => {
        this.requestedInstant = false;
        this.step = BOOKING_STEPS.DAY;
        this.view.renderStep(this);
        await this.showRequestedAppointment();
      }, button);
      if (button.hasAttribute('data-choose-instant')) return this.run(
        () => this.instantDiscoveryState === AVAILABILITY_STATES.ERROR ? this.refreshInstant() : this.chooseInstant(), button);
      if (button.hasAttribute('data-retry-call') && this.bookingToken) return this.startCallConnection();
      if (button.hasAttribute('data-join-call') && this.meetingUrl) return location.assign(this.meetingUrl);
      if (button.hasAttribute("data-change-form")) return this.changeForm();
      if (button.dataset.selectForm && this.step === BOOKING_STEPS.SELECT && this.group?.forms.some(form => form.slug === button.dataset.selectForm))
        return this.run(() => this.selectForm(button.dataset.selectForm), button);
      if (button.hasAttribute("data-dismiss-promotion"))
        this.querySelector(".sw-booking__promotion").hidden = true;
      if (button.hasAttribute("data-retry") && this.retryAction)
        return this.run(this.retryAction, button);
      if (button.hasAttribute("data-refresh-days"))
        return this.run(async () => {
          await this.loadAvailability();
          if (this.singleRowCalendar) await this.openFirstAvailableDay();
        }, button);
      if (button.hasAttribute("data-refresh-times"))
        return this.run(() => this.loadSlots(this.selectedDay), button);
      if (button.hasAttribute("data-back") && !this.bookingAttempt) {
        this.retryAction = null;
        this.querySelector("[data-retry]").hidden = true;
        this.querySelector("[data-error]").hidden = true;
        this.requestedDay = this.requestedInstant ? null : this.selectedDay;
        this.requestedSlotId = this.requestedInstant ? null : this.selectedSlot?.id || null;
        this.step = BOOKING_STEPS.DETAILS;
        this.selectedSlot = null;
        this.view.renderStep(this);
        this.updateDateLink(null);
        return;
      }
      if (
        button.hasAttribute("data-prev-month") ||
        button.hasAttribute("data-next-month")
      ) {
        if (this.bookingAttempt) return;
        const direction = button.hasAttribute("data-prev-month") ? -1 : 1;
        return this.run(() => this.changeMonth(direction), button);
      }
      if (
        button.dataset.day &&
        [BOOKING_STEPS.DAY, BOOKING_STEPS.TIME].includes(this.step) &&
        !this.bookingAttempt &&
        this.days.includes(button.dataset.day)
      )
        return this.run(() => this.loadSlots(button.dataset.day), button);
      if (
        button.dataset.slot &&
        this.step === BOOKING_STEPS.TIME &&
        !this.bookingAttempt
      ) {
        this.selectedSlot = this.slots.find(
          (slot) => slot.id === button.dataset.slot,
        );
        this.view.renderTimes(this);
        this.view.updateControls(this);
        this.querySelector("[data-book]").focus();
      }
      if (button.hasAttribute("data-book")) return this.run(() => this.book(), button);
    }
  }
  customElements.define("sw-booking-flow", BookingFlow);
})();
